'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { useReducedMotion, useSequenceClock } from '../use-animation-clock';
import { useMostlyInView, ShowcaseIcon, useReveal } from './scene-primitives';
import {
  wf,
  deliverableFiles,
  xf,
  Sf,
  DeliverableFile,
  Tf,
} from './deliverable-files';
import { ShowcaseSpinner } from './playback-controls';
import { ProductionIcon } from './deliverable-previews';
import {
  collectionFacts,
  productionFacts,
  referenceBrands,
} from './workflow-facts';
export function DeliverablesAnimation() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let e = useReducedMotion(),
    [t, n] = useMostlyInView(0.5),
    i = useSequenceClock({
      key: 'drive',
      duration: wf,
      running: n === !0,
      reduced: e,
    }),
    { restart: a } = i,
    o = i.t,
    s = Math.min(
      deliverableFiles.length,
      Math.max(0, Math.floor((o - xf) / Sf) + 1)
    );
  return (
    <div
      ref={t}
      className="deliverables-window sf overflow-hidden rounded-[14px]"
    >
      <div
        className={
          'flex h-12 items-center gap-2 border-b border-[var(--line)] px-3'
        }
      >
        <span
          aria-hidden={!0}
          className={'text-ink-4 flex items-center gap-0.5 max-sm:hidden'}
        >
          <ShowcaseIcon name={'chevronRight'} className={'size-4 rotate-180'} />
          <ShowcaseIcon name={'chevronRight'} className={'size-4'} />
        </span>
        <span
          className={
            'text-ink-2 flex h-8 min-w-0 items-center gap-2 rounded-md px-2.5 text-[13px] shadow-[inset_0_0_0_1px_var(--border)]'
          }
        >
          <ShowcaseIcon
            name={'folder'}
            className={'text-ink-3 size-4 shrink-0'}
          />
          <span className={'truncate'}>
            {translateShowcaseNode(showcaseCopy, 'casa-orvieto / box-tote')}
          </span>
        </span>
        <span
          className={
            'num text-ink-3 ml-auto flex shrink-0 items-center gap-2 text-[12.5px]'
          }
        >
          {translateShowcaseNode(
            showcaseCopy,
            s < deliverableFiles.length && o > xf ? (
              <ShowcaseSpinner className={'text-ink-3 size-3.5'} />
            ) : null
          )}
          {translateShowcaseNode(showcaseCopy, s)}
          {translateShowcaseNode(showcaseCopy, ' ')}
          {translateShowcaseNode(showcaseCopy, s === 1 ? 'item' : 'items')}
        </span>
        <span
          aria-hidden={!0}
          className={
            'flex shrink-0 items-center gap-0.5 rounded-md p-0.5 max-sm:hidden'
          }
        >
          <span
            className={
              'text-ink-4 grid size-7 place-items-center rounded-[5px]'
            }
          >
            <ProductionIcon name={'list'} className={'size-4'} />
          </span>
          <span
            className={
              'text-ink grid size-7 place-items-center rounded-[5px] bg-[var(--state-wash)]'
            }
          >
            <ProductionIcon name={'grid'} className={'size-4'} />
          </span>
        </span>
      </div>
      <div
        className={
          'deliverables-grid grid grid-cols-2 gap-2.5 p-3 sm:grid-cols-3 sm:gap-3 sm:p-4 lg:grid-cols-4'
        }
      >
        {translateShowcaseNode(
          showcaseCopy,
          deliverableFiles.map((e, t) => (
            <DeliverableFile key={e.name} file={e} index={t} t={o} />
          ))
        )}
      </div>
      <ul className={'sr-only'}>
        {translateShowcaseNode(
          showcaseCopy,
          deliverableFiles.map((e) => (
            <li key={e.name}>
              {translateShowcaseNode(showcaseCopy, e.kind)}
              {translateShowcaseNode(showcaseCopy, ': ')}
              {translateShowcaseNode(showcaseCopy, Tf(e.kind))}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
export const Of = [1500, 3900, 5e3, 5900, 6600, 7400, 8100];
export const kf = collectionFacts.stages.find((e) => e.key === 'approve');
export const Af = (e) => ({
  workflow: e,
  does:
    (
      productionFacts.workflows.find((t) => t.name === e) ??
      kf?.workflows.find((t) => t.name === e)
    )?.does ?? '',
});
export const jf = 2200;
export const Mf = {
  key: 'review',
  label: 'Review',
  icon: 'search',
  brand: 'lisiere',
  ...Af('Review the Collection'),
  ask: 'FW27 against the plan',
  title: 'FW27 Collection Review',
  ms: 5600,
};
export const Nf = [
  Mf,
  {
    key: 'lock',
    label: 'Lock',
    icon: 'lock',
    brand: 'lisiere',
    ...Af('Lock the Collection'),
    ask: 'FW27, freeze packet',
    title: 'FW27 Freeze Packet',
    ms: 5200,
  },
  {
    key: 'techpack',
    label: 'Tech pack',
    icon: 'ruler',
    brand: 'orvieto',
    ...Af('Build a Detailed Tech Pack'),
    ask: 'box tote in Cognac',
    title: 'Box Tote Tech Pack',
    ms: 8200,
  },
  {
    key: 'sourcing',
    label: 'Sourcing',
    icon: 'tag',
    brand: 'orvieto',
    ...Af('Plan Sourcing and Costing'),
    ask: 'box tote, 300 units',
    title: 'Box Tote Sourcing and Costing',
    ms: 6e3,
  },
  {
    key: 'rfq',
    label: 'RFQ',
    icon: 'mail',
    brand: 'orvieto',
    ...Af('Prepare a Supplier RFQ'),
    ask: 'for the recommended path',
    title: 'Supplier RFQ',
    ms: 5600,
  },
  {
    key: 'sample',
    label: 'Sample',
    icon: 'camera',
    brand: 'orvieto',
    ...Af('Review a Product Sample'),
    ask: 'first proto, photos attached',
    title: 'Proto 1 Redlines',
    ms: 6e3,
  },
  {
    key: 'readiness',
    label: 'Readiness',
    icon: 'clipboard',
    brand: 'orvieto',
    ...Af('Run a Production Readiness Review'),
    ask: 'tote, before bulk',
    title: 'Production Readiness Review',
    ms: 6400,
  },
  {
    key: 'handoff',
    label: 'Handoff',
    icon: 'layers',
    brand: 'orvieto',
    ...Af('Prepare the Factory Handoff'),
    ask: 'tote, latest approved versions',
    title: 'Factory Handoff',
    ms: 5600,
  },
];
export const Pf = (e) => Nf[e] ?? Mf;
export const Ff = [
  {
    brand: 'lisiere',
    label: `${referenceBrands.lisiere.name} · Approve`,
    keys: ['review', 'lock'],
  },
  {
    brand: 'orvieto',
    label: `${referenceBrands.orvieto.name} · Produce`,
    keys: ['techpack', 'sourcing', 'rfq', 'sample', 'readiness', 'handoff'],
  },
];
export function ProductionStepRail({ value: e, onChange: t, progress: n }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let [r, i] = useReveal({
      once: !0,
    }),
    a = React.useRef(null),
    o = Nf.findIndex((t) => t.key === e);
  return (
    React.useEffect(() => {
      let e = a.current;
      if (!e || e.scrollWidth <= e.clientWidth) return;
      let t = e.querySelector('[aria-current="step"]');
      t &&
        e.scrollTo({
          left: t.offsetLeft - (e.clientWidth - t.offsetWidth) / 2,
          behavior: 'smooth',
        });
    }, [e]),
    (
      <div
        ref={r}
        data-in={i === null ? void 0 : String(i)}
        className={'min-w-0'}
      >
        <div
          ref={a}
          role={'group'}
          aria-label={translateShowcaseNode(
            showcaseCopy,
            'Approve and produce steps'
          )}
          className={'pd-rail relative -mx-1 overflow-x-auto px-1'}
        >
          <div className={'flex w-max min-w-full items-end'}>
            {translateShowcaseNode(
              showcaseCopy,
              Ff.map((e, r) => (
                <div
                  key={e.label}
                  className={'flex min-w-0 flex-col gap-1'}
                  style={{
                    flexGrow: e.keys.length,
                  }}
                >
                  <span
                    className={`text-ink-4 h-4 truncate text-[12px] leading-4 font-medium ${r > 0 ? 'pl-5 sm:pl-6' : 'pl-1 sm:pl-2'}`}
                  >
                    {translateShowcaseNode(showcaseCopy, e.label)}
                  </span>
                  <div className={'flex items-center'}>
                    {translateShowcaseNode(
                      showcaseCopy,
                      e.keys.map((e) => {
                        let r = Nf.findIndex((t) => t.key === e),
                          i = Nf[r];
                        if (!i) return null;
                        let a = r === o,
                          s = r < o,
                          c = r <= o ? 1 : r === o + 1 ? (n ?? 0) : 0;
                        return (
                          <div
                            key={e}
                            className={`flex items-center ${r > 0 ? 'flex-1' : ''}`}
                          >
                            {translateShowcaseNode(
                              showcaseCopy,
                              r > 0 ? (
                                <span
                                  aria-hidden={!0}
                                  className={
                                    'relative mx-0.5 h-px min-w-4 flex-1 bg-[var(--border)] sm:mx-1 sm:min-w-3'
                                  }
                                >
                                  <span
                                    className={`absolute inset-0 origin-left bg-[var(--primary)] ${r === o + 1 && n !== null ? '' : 'transition-transform duration-500 ease-[var(--ease)]'}`}
                                    style={{
                                      transform: `scaleX(${c})`,
                                    }}
                                  />
                                </span>
                              ) : null
                            )}
                            <button
                              type={'button'}
                              aria-current={a ? 'step' : void 0}
                              onClick={() => t(i.key)}
                              className={`ghost press flex h-10 shrink-0 items-center gap-2 px-1 text-[13px] whitespace-nowrap sm:px-1.5 ${a ? '!text-ink font-medium' : ''}`}
                            >
                              <span
                                className={`grid size-7 shrink-0 place-items-center rounded-full transition-[background-color,color,box-shadow] duration-200 ${a ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : s ? 'text-ink bg-[var(--state-wash)]' : 'text-ink-3 shadow-[inset_0_0_0_1px_var(--border)]'}`}
                              >
                                <ShowcaseIcon
                                  name={i.icon}
                                  draw={'in-view'}
                                  className={'size-4'}
                                  style={{
                                    '--i': String(r),
                                  }}
                                />
                              </span>
                              <span className={a ? '' : 'lg:max-xl:sr-only'}>
                                {translateShowcaseNode(showcaseCopy, i.label)}
                              </span>
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    )
  );
}
